import { useState, useEffect, useCallback } from 'react';
import { Button, Select } from '../../components/ui';
import api from '../../services/api';

const MAX_SMS = 160;

function CharCount({ text }) {
  const len  = text?.length || 0;
  const msgs = Math.ceil(len / MAX_SMS) || 1;
  const color = len > MAX_SMS ? '#f06060' : len > 130 ? '#e8a43a' : 'var(--text-muted)';
  return (
    <span style={{ fontSize: 11, color }}>
      {len}/{MAX_SMS} chars {msgs > 1 ? `(${msgs} SMS)` : ''}
    </span>
  );
}

function StatCard({ label, value, color = 'var(--accent)' }) {
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '14px 18px' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: 'var(--font-display)' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function SMSPage() {
  const [tab, setTab]           = useState('compose'); // compose | bulk | logs
  const [message, setMessage]   = useState('');
  const [phone, setPhone]       = useState('');
  const [sending, setSending]   = useState(false);
  const [result, setResult]     = useState(null);
  const [stats, setStats]       = useState(null);
  const [logs, setLogs]         = useState([]);
  const [logsLoading, setLL]    = useState(false);
  const [cohorts, setCohorts]   = useState([]);

  // Bulk state
  const [audience, setAudience]   = useState('all_enrolled');
  const [cohortId, setCohortId]   = useState('');
  const [bulkMessage, setBulk]    = useState('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkResult, setBulkResult]   = useState(null);

  useEffect(() => {
    api.get('/admin/sms/stats').then(r => setStats(r.data.data)).catch(() => {});
    api.get('/admin/sms/cohorts').then(r => setCohorts(r.data.data || [])).catch(() => {});
  }, []);

  const loadLogs = useCallback(async () => {
    setLL(true);
    try {
      const res = await api.get('/admin/sms/logs', { params: { limit: 30 } });
      setLogs(res.data.data || []);
    } catch { setLogs([]); } finally { setLL(false); }
  }, []);

  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

  const sendSingle = async () => {
    if (!phone.trim() || !message.trim()) return;
    setSending(true); setResult(null);
    try {
      const res = await api.post('/admin/sms/send', { phone, message });
      setResult({ success: true, ...res.data.data });
      setPhone(''); setMessage('');
      api.get('/admin/sms/stats').then(r => setStats(r.data.data)).catch(() => {});
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.error || 'Send failed' });
    } finally { setSending(false); }
  };

  const sendBulk = async () => {
    if (!bulkMessage.trim()) return;
    setBulkSending(true); setBulkResult(null);
    try {
      const payload = { message: bulkMessage, audience };
      if (audience === 'cohort') payload.cohort_id = cohortId;
      const res = await api.post('/admin/sms/bulk', payload);
      setBulkResult({ success: true, ...res.data.data });
      setBulk('');
      api.get('/admin/sms/stats').then(r => setStats(r.data.data)).catch(() => {});
    } catch (err) {
      setBulkResult({ success: false, error: err.response?.data?.error || 'Bulk send failed' });
    } finally { setBulkSending(false); }
  };

  const tabBtn = (t) => ({
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, marginBottom: -1, transition: 'all 0.15s',
  });

  const STATUS_COLORS = { sent: '#3ecf8e', failed: '#f06060', logged: '#e8a43a', dev: '#6b7280' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 2 }}>SMS Center</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>MTN Rwanda + Africa's Talking · auto-routing by network</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'rgba(78,156,245,0.12)', color: '#4e9cf5', border: '1px solid rgba(78,156,245,0.3)' }}>
            📡 MTN Primary
          </div>
          <div style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'rgba(62,207,142,0.12)', color: '#3ecf8e', border: '1px solid rgba(62,207,142,0.3)' }}>
            🌍 AT Fallback
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px,1fr))', gap: 12 }}>
          <StatCard label="Total Sent"    value={stats.sent}       color="#3ecf8e"/>
          <StatCard label="Failed"        value={stats.failed}     color="#f06060"/>
          <StatCard label="Last 24h"      value={stats.last_24h}   color="var(--accent)"/>
          <StatCard label="Via MTN"       value={stats.via_mtn}    color="#4e9cf5"/>
          <StatCard label="Via AT"        value={stats.via_at}     color="#3ecf8e"/>
          <StatCard label="Dev Logged"    value={stats.dev_logged} color="var(--text-muted)"/>
        </div>
      )}

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--border)' }}>
        <button style={tabBtn('compose')} onClick={() => setTab('compose')}>💬 Single SMS</button>
        <button style={tabBtn('bulk')}    onClick={() => setTab('bulk')}>📢 Bulk SMS</button>
        <button style={tabBtn('logs')}    onClick={() => setTab('logs')}>📋 Logs</button>
      </div>

      {/* ── COMPOSE SINGLE ── */}
      {tab === 'compose' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 520 }}>
          <div>
            <label style={labelStyle}>Recipient Phone</label>
            <input value={phone} onChange={e => setPhone(e.target.value)}
              placeholder="+250 7xx xxx xxx"
              style={inputStyle}/>
            {phone && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Network: <strong>{phone.replace(/\s/g,'').match(/^(\+250|0)?(078|079|072|073)/) ? 'MTN' : phone.replace(/\s/g,'').match(/^(\+250|0)?(075|076)/) ? 'Airtel' : '—'}</strong>
              </div>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Message</label>
              <CharCount text={message}/>
            </div>
            <textarea value={message} onChange={e => setMessage(e.target.value)} rows={4}
              placeholder="Type your SMS message..."
              style={{ ...inputStyle, resize: 'vertical' }}/>
          </div>

          {/* Quick templates */}
          <div>
            <label style={labelStyle}>Quick Templates</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {[
                { label: 'Welcome', text: 'Welcome to MilleHills! Your account is ready. Visit millehills.rw to get started.' },
                { label: 'Reminder', text: 'Reminder: Your MilleHills program starts soon. Please confirm your attendance at millehills.rw' },
                { label: 'Payment due', text: 'Hi, your MilleHills program fee is due. Please make payment to secure your spot. millehills.rw' },
              ].map(t => (
                <button key={t.label} onClick={() => setMessage(t.text)}
                  style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, cursor: 'pointer', background: 'var(--bg-elevated)', border: '1px solid var(--border)', color: 'var(--text-muted)', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {result && (
            <div style={{ padding: '12px 16px', borderRadius: 8, fontSize: 13,
              background: result.success ? 'rgba(62,207,142,0.1)' : 'rgba(240,96,96,0.1)',
              border: `1px solid ${result.success ? 'rgba(62,207,142,0.3)' : 'rgba(240,96,96,0.3)'}`,
              color: result.success ? '#3ecf8e' : '#f06060' }}>
              {result.success
                ? `✓ SMS sent via ${result.provider?.toUpperCase() || 'provider'}${result.reference ? ` · Ref: ${result.reference}` : ''}`
                : `✗ Failed: ${result.error}`}
            </div>
          )}

          <Button onClick={sendSingle} loading={sending} disabled={!phone.trim() || !message.trim()}>
            Send SMS →
          </Button>
        </div>
      )}

      {/* ── BULK SMS ── */}
      {tab === 'bulk' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 560 }}>
          <div style={{ padding: '12px 16px', background: 'rgba(232,164,58,0.08)', border: '1px solid rgba(232,164,58,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
            ⚠️ Bulk SMS is charged per message. Make sure your content is accurate before sending.
          </div>

          <div>
            <label style={labelStyle}>Audience</label>
            <select value={audience} onChange={e => setAudience(e.target.value)} style={inputStyle}>
              <option value="all_enrolled">All Enrolled Students</option>
              <option value="cohort">Specific Cohort</option>
              <option value="partners">Partner Institutions</option>
              <option value="trainers">Trainers</option>
            </select>
          </div>

          {audience === 'cohort' && (
            <div>
              <label style={labelStyle}>Select Cohort</label>
              <select value={cohortId} onChange={e => setCohortId(e.target.value)} style={inputStyle}>
                <option value="">Choose cohort...</option>
                {cohorts.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.program_title} — {c.cohort_name} ({c.enrolled_count} enrolled)
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>Message</label>
              <CharCount text={bulkMessage}/>
            </div>
            <textarea value={bulkMessage} onChange={e => setBulk(e.target.value)} rows={5}
              placeholder="Write your message to all recipients..."
              style={{ ...inputStyle, resize: 'vertical' }}/>
          </div>

          {/* Preview */}
          {bulkMessage && (
            <div style={{ padding: '12px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Preview</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{bulkMessage}</div>
            </div>
          )}

          {bulkResult && (
            <div style={{ padding: '14px 16px', borderRadius: 8, fontSize: 13,
              background: bulkResult.success ? 'rgba(62,207,142,0.1)' : 'rgba(240,96,96,0.1)',
              border: `1px solid ${bulkResult.success ? 'rgba(62,207,142,0.3)' : 'rgba(240,96,96,0.3)'}`,
              color: bulkResult.success ? '#3ecf8e' : '#f06060' }}>
              {bulkResult.success
                ? `✓ Sent to ${bulkResult.sent} recipients. Failed: ${bulkResult.failed || 0}`
                : `✗ ${bulkResult.error}`}
            </div>
          )}

          <Button onClick={sendBulk} loading={bulkSending}
            disabled={!bulkMessage.trim() || (audience === 'cohort' && !cohortId)}
            style={{ background: '#e8a43a', color: '#080a0d', border: 'none' }}>
            📢 Send Bulk SMS
          </Button>
        </div>
      )}

      {/* ── LOGS ── */}
      {tab === 'logs' && (
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13 }}>Recent SMS Logs</span>
            <button onClick={loadLogs} style={{ fontSize: 11, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>↻ Refresh</button>
          </div>
          {logsLoading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>No SMS logs yet</div>
          ) : (
            <div>
              {logs.map(log => (
                <div key={log.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'monospace', color: 'var(--text-primary)' }}>{log.phone}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20,
                        background: `${STATUS_COLORS[log.status] || '#888'}18`,
                        color: STATUS_COLORS[log.status] || '#888',
                        border: `1px solid ${STATUS_COLORS[log.status] || '#888'}44`,
                        textTransform: 'capitalize' }}>{log.status}</span>
                      <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border)', textTransform: 'uppercase' }}>{log.provider}</span>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{log.message?.slice(0, 100)}{log.message?.length > 100 ? '…' : ''}</div>
                    {log.error_message && <div style={{ fontSize: 11, color: '#f06060', marginTop: 2 }}>{log.error_message}</div>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right' }}>
                    <div>{new Date(log.created_at).toLocaleDateString('en-RW', { day: 'numeric', month: 'short' })}</div>
                    <div>{new Date(log.created_at).toLocaleTimeString('en-RW', { hour: '2-digit', minute: '2-digit' })}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: 6 };
const inputStyle = { width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, boxSizing: 'border-box', fontFamily: 'inherit' };